'use client'

import { useMemo, useState } from 'react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'

import Hero from '@/components/content/common/Hero'
import Container from '@/components/structure/Container'
import { StackHeroContent } from '@/components/content/stacks/stack/hero/StackHero'
import StackCarousel from '@/components/content/stacks/stack/hero/StackCarousel'
import StackPicker from '@/components/content/stacks/root/hero/StackPicker'
import DotsPagination from '@/components/content/modules/hero/controls/DotsPagination'

import { getBrandColors, hexToRgba } from '@/lib/utils'
import type { App, Stack, StackAppsApp } from '@/payload-types'

type StacksHeroProps = {
  stacks: Stack[]
}

export default function StacksHero({ stacks }: StacksHeroProps) {
  const [playing, setPlaying] = useState(true)
  const [selectedStack, setSelectedStack] = useState<Stack | null>(stacks[0] ?? null)
  const [selectedApp, setSelectedApp] = useState<App | null>(null)

  // Get apps for the currently selected stack
  const selectedStackApps = useMemo(() => {
    if (!selectedStack?.stackApps?.docs) return []
    return (selectedStack.stackApps.docs as StackAppsApp[]).map((stackApp) => stackApp.app) as App[]
  }, [selectedStack])

  const selectedAppItem = useMemo(() => {
    const defaultColors = {
      brandBackgroundColor: '#FFF',
      branding: {
        primaryColor: '#ff34e7',
        secondaryColor: '#ff3465',
      },
    }

    if (!selectedApp) {
      return defaultColors
    }

    const { primaryColor, secondaryColor } = getBrandColors(selectedApp)

    if (!primaryColor || !secondaryColor) {
      return defaultColors
    }

    return {
      brandBackgroundColor: selectedApp.brandBackgroundColor,
      branding: {
        primaryColor,
        secondaryColor,
      },
    }
  }, [selectedApp])

  const backgroundGradient = `radial-gradient(50% 100% at 35% 0%, ${hexToRgba(selectedAppItem?.branding.primaryColor, 0.4)} 0%, rgba(0, 0, 0, 0.00) 80%), radial-gradient(50% 100% at 70% 0%, ${hexToRgba(selectedAppItem?.branding.secondaryColor, 0.4)} 0%, rgba(0, 0, 0, 0.00) 80%), #000`

  return (
    <Hero className="pt-8 md:pt-12">
      {({ animations }) => (
        <Motion>
          <>
            <m.div
              className="absolute inset-0 -z-10"
              initial={false}
              animate={{
                background: backgroundGradient,
              }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />

            {selectedStack && (
              <Container
                height="fill"
                className="relative z-10 flex h-full flex-col items-center lg:flex-row lg:items-start lg:justify-between lg:pr-20"
              >
                <m.div
                  style={animations}
                  className="flex h-full w-fit items-center pt-14 lg:pb-20 lg:pt-12"
                >
                  <div className="flex h-fit flex-col">
                    <StackPicker
                      stacks={stacks}
                      selectedStack={selectedStack}
                      setSelectedStack={setSelectedStack}
                    />
                    <StackHeroContent stack={selectedStack} apps={selectedStackApps} type="root" />
                  </div>
                </m.div>

                <DotsPagination
                  activeIndex={Math.max(0, stacks.indexOf(selectedStack))}
                  setActiveIndex={(index) => setSelectedStack(stacks[index])}
                  total={stacks.length}
                  position="absolute left-1/2 -translate-x-1/2 bottom-7 lg:bottom-12 lg:translate-x-0 lg:left-0"
                  playing={playing}
                />

                <StackCarousel
                  apps={selectedStackApps}
                  selectedApp={selectedApp}
                  setSelectedApp={setSelectedApp}
                  vertical={true}
                  onMouseEnter={() => setPlaying(false)}
                  onMouseLeave={() => setPlaying(true)}
                />
              </Container>
            )}
          </>
        </Motion>
      )}
    </Hero>
  )
}
